import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronRight, Plus } from 'lucide-react';
import { NexaGlyph } from '@/components/nexa-mark/nexa-glyph';
import { useNow } from '@/components/allocation/use-now';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { declareSourceState } from '@/routes/declare/declare-route.state';
import { PageHeader } from '@/routes/disruptions/board-chrome';
import { isOpen } from '@/routes/disruptions/board-model';
import { FlightWhen } from '@/routes/disruptions/flight-when';
import { NEXA_QUIET } from '@/routes/disruptions/operar/nexa-guide';
import { ACTION_TONE } from '@/routes/disruptions/operar/views/cancelled/action-tone';
import { PnrOperationCenter } from '@/routes/disruptions/roster-pnr-drawer';
import { NETWORK_SCOPE } from '@/routes/disruptions/situation';
import { useOpenBoard, type IOpenBoard } from '@/routes/disruptions/use-open-board';
import { PassengerSearch } from '@/routes/passengers/passenger-search';
import { needsParts, needsYou, type INeed } from './needs-you';
import { useDashboardCounters } from './use-dashboard';
import type { DashboardCounters } from './types';

// The operator's HOME — «Resumen» (es; Dashboard en) — per docs/ui/operator-ui-tds.md §4.1
// + §8 (j1): a quick look at NOW (today) and, one click away, what has to be done now
// (PO 2026-11-01). It does NOT repeat the disruptions list — /disruptions owns it
// («se solapan demasiado el dashboard y las disrupciones»); this page summarises it and
// lists only what asks for a person.
//
// Layout (frontend-conventions §18: full width; the middle scrolls, the header band and
// the totals footer stay):
//   1. HEADER BAND (board-chrome.tsx, the one /disruptions draws) — «Resumen», today's
//      date, and a live clock (the operator's local time, by the second) on the right.
//   2. SEARCH + DECLARE — the universal search (passengers/passenger-search: passengers,
//      PNRs, flights) and beside it ONE big «Declarar disrupción» button: declaring is
//      one action («al final declarar una disrupción hoy es una acción única») — one or
//      several flights, a file or loose PNRs are chosen inside the flow (/express, its
//      step-1 sources). While a search runs the results ARE the page: the summary goes
//      away (`onActiveChange`), the flights read as on /flights with their own action.
//   3. TODAY'S SUMMARY — one card: Nexa's navy band on top (how many things need the
//      operator, today's numbers, «Ver disrupciones»), then in white the list of WHAT:
//        - every recent open disruption whose provider report is over the policy
//          maximum and waits for a decision («Decidir»);
//        - the open disruption with the most passengers unresolved («Operar») — the
//          same «Lo que sigue» rule as /disruptions (`nextOnBoard`), so the first row
//          here is the disruption the board's card names;
//        - the queues that wait for a person: manual review, stuck reservations
//          («Revisar»).
//      The first row is the filled «next» action; the rest wear Operar's action tones.
//   4. TOTALS FOOTER — the all-time counters (Redis O(1) read-model), always in view.
//
// KPI DATA SOURCES (us3-group/nexa#746 — see docs/kpi-definitions.md):
// - the summary's numbers and disruption rows ← disruptions overview (real-time; the
//   SAME `useOpenBoard` as /disruptions, so «sin resolver» matches its card)
// - the queues and the totals ← dashboard counters (eventual, Redis)
// These use DIFFERENT data sources and may show small discrepancies during
// high-throughput periods. This is expected behavior, not a bug.

export function DashboardRoute() {
  const { t, i18n } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  // The selected search hit — opens the full PNR operation center in place.
  const [activeHit, setActiveHit] = useState<DisruptionPnrGroupRow | null>(null);
  // A search is showing its results — the summary steps aside.
  const [searching, setSearching] = useState(false);
  // The whole network (the header airport scope still narrows it), as /disruptions
  // opens on it — so «Lo que sigue» and «sin resolver» are the board's own.
  const board = useOpenBoard({ enabled: true, scope: NETWORK_SCOPE });
  const counters = useDashboardCounters();
  const today = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(board.now);

  return (
    <main className="flex h-full flex-col overflow-hidden">
      <PageHeader title={tc('nav.dashboard')} testId="dashboard-header" actions={<LiveClock />}>
        <p className="text-lg font-medium text-foreground/80" data-testid="dashboard-today">
          {t('dashboard.today', { date: today })}
        </p>
      </PageHeader>

      <div className="density-page min-h-0 flex-1 overflow-y-auto p-4 lg:p-6" data-testid="dashboard-scroll">
        <div className="flex w-full flex-col gap-5">
          <PassengerSearch onOpen={setActiveHit} onActiveChange={setSearching} aside={<DeclareButton />} />
          {searching ? null : <TodaySummary board={board} counters={counters.data} />}
        </div>
      </div>

      <TotalsFooter counters={counters.data} loading={counters.isLoading} />

      {activeHit ? (
        <PnrOperationCenter
          scope={{ kind: 'case', caseUrn: activeHit.caseUrn }}
          group={activeHit}
          onClose={() => setActiveHit(null)}
        />
      ) : null}
    </main>
  );
}

// -------------------------------------------------------------------- header

/** The operator's local time, by the second — its own component, so only it re-renders
 *  every tick. Not a live region: a screen reader is not read a clock every second. */
function LiveClock() {
  const { t, i18n } = useTranslation('admin');
  const now = useNow(true, 1000);
  const time = new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).format(now);
  return (
    <p className="flex items-baseline gap-2" data-testid="dashboard-clock">
      <time
        dateTime={new Date(now).toISOString()}
        className="font-mono text-3xl font-bold tabular-nums tracking-tight"
      >
        {time}
      </time>
      <span className="text-xs text-muted-foreground">{t('dashboard.clock')}</span>
    </p>
  );
}

/** THE way in to declaring — big, filled, beside the search. One flight or several, a
 *  file or loose PNRs are the flow's step-1 sources (`declare-route.state.ts`); the
 *  button opens it on its departures board. */
function DeclareButton() {
  const { t } = useTranslation('admin');
  return (
    <Link
      to="/express"
      state={declareSourceState('BOARD')}
      data-testid="dashboard-declare"
      className="group inline-flex h-16 flex-[1_0_auto] items-center gap-3.5 whitespace-nowrap rounded-xl bg-primary px-5 text-primary-foreground shadow-md shadow-primary/25 transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30"
    >
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15"
        aria-hidden
      >
        <Plus className="h-5 w-5" />
      </span>
      <span className="flex-1 text-lg font-extrabold">{t('dashboard.declare')}</span>
      <ChevronRight
        className="h-5 w-5 shrink-0 transition-transform group-hover:translate-x-0.5"
        aria-hidden
      />
    </Link>
  );
}

// ------------------------------------------------------------------- summary

/** A count as the board prints it: «12+» when it is a floor. */
function countText(n: number, floor: boolean): string {
  return `${n.toLocaleString()}${floor ? '+' : ''}`;
}

/** Nexa's navy band (how many things need you, today's numbers) over the white list of
 *  what they are. */
function TodaySummary({
  board,
  counters,
}: {
  board: IOpenBoard;
  counters: DashboardCounters | undefined;
}) {
  const { t, i18n } = useTranslation('admin');
  const { groups, kpis, overview } = board;
  const truncated = overview.data?.truncated ?? false;
  const items = useMemo(() => needsYou(board, counters, t), [board, counters, t]);

  if (overview.isError) {
    return (
      <EmptyState
        title={t('disruptions:board.error.title')}
        description={t('disruptions:board.error.description')}
      />
    );
  }
  if (overview.isLoading) {
    return <Skeleton className="h-64 w-full rounded-2xl" />;
  }

  const recentOpen = [...groups.today, ...groups.yesterday].filter(isOpen);
  const unresolved = kpis.unresolved;
  const numbers: { id: string; value: string; tone?: string }[] = [
    { id: 'openToday', value: groups.today.filter(isOpen).length.toLocaleString() },
    {
      id: 'unresolved',
      value:
        unresolved.n === null
          ? t('disruptions:board.queue.noData')
          : countText(unresolved.n, truncated || unresolved.partial),
      ...(unresolved.n !== null && unresolved.n > 0 ? { tone: 'text-rose-300' } : {}),
    },
    (() => {
      const n = recentOpen.filter((r) => !!r.flightStatus?.outOfPolicy).length;
      return { id: 'decisions', value: n.toLocaleString(), ...(n > 0 ? { tone: 'text-rose-300' } : {}) };
    })(),
    {
      id: 'resolvedToday',
      value: groups.today.filter((r) => !isOpen(r)).length.toLocaleString(),
      tone: 'text-emerald-300',
    },
  ];

  return (
    <section
      aria-label={t('dashboard.summary.region')}
      className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
      data-testid="dashboard-summary"
    >
      <div className="relative flex flex-wrap items-center gap-x-8 gap-y-4 overflow-hidden bg-nexa-navy px-5 py-5 text-white sm:px-6">
        <NexaGlyph
          tone="light"
          className="pointer-events-none absolute -right-8 -top-16 h-56 w-60 opacity-10"
        />
        <div className="relative min-w-0 flex-[999_1_16rem]">
          <p className="text-xs font-extrabold uppercase tracking-widest text-nexa-cyan-soft">
            {t('dashboard.summary.label')}
          </p>
          <h2 className="mt-1.5 text-2xl font-bold" data-testid="dashboard-summary-headline">
            {items.length === 0
              ? t('dashboard.summary.headlineNone')
              : t('dashboard.summary.headline', {
                  list: new Intl.ListFormat(i18n.language, { type: 'conjunction' }).format(
                    needsParts(items).map((part) => t(`dashboard.summary.part.${part.key}`, { count: part.count })),
                  ),
                })}
          </h2>
        </div>
        <dl className="relative flex flex-wrap gap-x-8 gap-y-3">
          {numbers.map((k) => (
            <div key={k.id} data-testid={`dashboard-kpi-${k.id}`}>
              <dt className="max-w-[11rem] font-mono text-[10px] uppercase tracking-widest text-nexa-mist">
                {t(`dashboard.summary.kpi.${k.id}`)}
              </dt>
              <dd className={cn('mt-1.5 font-mono text-3xl font-bold leading-none tabular-nums', k.tone)}>
                {k.value}
              </dd>
            </div>
          ))}
        </dl>
        <Button asChild variant="outline" className={cn(NEXA_QUIET, 'relative h-10 gap-1 font-semibold')}>
          <Link to="/disruptions" data-testid="dashboard-see-disruptions">
            {t('dashboard.summary.seeDisruptions')}
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Link>
        </Button>
      </div>

      {items.length === 0 ? (
        <p className="px-5 py-5 text-sm text-muted-foreground sm:px-6" data-testid="dashboard-needs-none">
          {t('dashboard.needs.none')}
        </p>
      ) : (
        <ul className="divide-y divide-border/60" data-testid="dashboard-needs">
          {items.map((item) => (
            <NeedRow key={item.id} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}

function NeedRow({ item }: { item: INeed }) {
  return (
    <li
      className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-4 sm:px-6"
      data-testid={`dashboard-need-${item.id}`}
      data-kind={item.kind}
      data-tone={item.tone}
    >
      <div className="min-w-0 flex-[999_1_20rem]">
        <p className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          {item.pending ? (
            // a span, not <Skeleton> (a div): this sits inside a <p>
            <span aria-hidden className="inline-block h-6 w-28 animate-pulse rounded-md bg-muted" />
          ) : (
            <span className={cn('text-xl font-bold', item.mono && 'font-mono tracking-tight')}>
              {item.title}
            </span>
          )}
          {item.sub ? <span className="text-sm text-muted-foreground">{item.sub}</span> : null}
          {item.when ? (
            <FlightWhen
              departureAt={item.when.departureAt}
              origin={item.when.origin}
              className="text-sm font-medium text-foreground/80"
            />
          ) : null}
          {item.chip ? (
            <span
              className={cn(
                'rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide',
                item.chip.className,
              )}
            >
              {item.chip.text}
            </span>
          ) : null}
        </p>
        <p
          className={cn(
            'mt-1 flex items-center gap-1.5 text-sm',
            item.alert ? 'font-medium text-rose-700 dark:text-rose-400' : 'text-muted-foreground',
          )}
        >
          {item.alert ? <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden /> : null}
          {item.why}
        </p>
      </div>
      <Button
        asChild
        variant="outline"
        className={cn('h-11 min-w-[10rem] flex-none justify-between gap-2 font-bold', ACTION_TONE[item.tone])}
      >
        <Link to={item.to} state={item.state} data-testid={`dashboard-need-${item.id}-open`}>
          {item.action}
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Link>
      </Button>
    </li>
  );
}

// -------------------------------------------------------------------- totals

/** Relative time for the counters' staleness, from the existing translation keys. */
function relativeTimeForStaleness(
  iso: string | null | undefined,
  t: ReturnType<typeof useTranslation<'admin'>>['t'],
): string | null {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  const secs = Math.max(0, Math.floor((Date.now() - at) / 1000));
  if (secs < 60) return t('time.secondsAgo', { n: secs });
  if (secs < 3600) return t('time.minutesAgo', { n: Math.floor(secs / 60) });
  return t('time.hoursAgo', { n: Math.floor(secs / 3600) });
}

/** The all-time totals (Redis O(1) read-model) — the page's footer, always in view
 *  while the middle scrolls. */
function TotalsFooter({
  counters,
  loading,
}: {
  counters: DashboardCounters | undefined;
  loading: boolean;
}) {
  const { t } = useTranslation('admin');
  // NEX-603: staleness when the counters come from the read-model.
  const staleness = relativeTimeForStaleness(counters?.computedAt, t);
  const num = (n: number | undefined): string => (typeof n === 'number' ? n.toLocaleString() : '—');
  const rows: { id: string; value: string; label: string }[] = [
    {
      id: 'openCases',
      value: num(counters?.openCases),
      label: t('dashboard.totals.openCases', { count: counters?.openCases ?? 0 }),
    },
    {
      id: 'passengers',
      value: num(counters?.passengers),
      label: t('dashboard.totals.passengers', { count: counters?.passengers ?? 0 }),
    },
    {
      id: 'paxInProgress',
      value: num(counters?.passengersInProgress),
      label: t('dashboard.totals.inProgress'),
    },
    {
      id: 'rooms',
      value: `${num(counters?.roomsBooked)}/${num(counters?.roomsRequired)}`,
      label: t('dashboard.totals.rooms'),
    },
    {
      id: 'stuckReservations',
      value: num(counters?.stuckReservations),
      label: t('dashboard.totals.stuck', { count: counters?.stuckReservations ?? 0 }),
    },
  ];
  return (
    <footer
      aria-label={t('dashboard.totals.label')}
      className="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-1 border-t border-border bg-muted/40 px-4 py-2.5 text-[13px] text-muted-foreground lg:px-6"
      data-testid="dashboard-totals"
    >
      <span className="font-mono text-[10px] font-bold uppercase tracking-widest">
        {t('dashboard.totals.label')}
      </span>
      {rows.map((row) => (
        <span key={row.id} className="inline-flex items-baseline" data-testid={`dashboard-total-${row.id}`}>
          {loading ? (
            <span aria-hidden className="inline-block h-4 w-8 animate-pulse self-center rounded bg-muted" />
          ) : (
            <strong className="font-mono font-bold tabular-nums text-foreground">{row.value}</strong>
          )}
          <span className="whitespace-pre">{' '}</span>
          {row.label}
        </span>
      ))}
      {staleness ? (
        <span className="ml-auto text-xs" data-testid="dashboard-totals-computed-at">
          {t('dashboard.totals.computedAt', { when: staleness })}
        </span>
      ) : null}
    </footer>
  );
}
